import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@/pages/strategic/hooks/useAutoSaveParams';

/**
 * Custom hook for Lifecycle Financial Forecast table auto-save functionality
 *
 * Manages auto-save detection and restoration for Lifecycle table with:
 * - Polling for data changes every 500ms
 * - Project-specific auto-save by including project ID
 * - User edit tracking to prevent false auto-save triggers
 *
 */
export const useLifecycleTableAutoSave = (
  isEditing: boolean,
  lifecycleData: any[],
  projectId: string,
  DCTRef: React.RefObject<any>,
  setLifecycleData: (data: any) => void,
) => {
  // Auto-save state
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [editData, setEditData] = useState<any[]>([]);
  const isManualUpdateRef = useRef(false);
  const hasUserEditedRef = useRef(false);

  const initialParamsRef = useRef<{
    tableData: any[];
    projectId: string;
  }>({
    tableData: [],
    projectId: '',
  });

  // Auto-save: construct unique pageKey by appending project ID
  // Each project has separate auto-save storage
  const lifecyclePageKey = useMemo(() => {
    if (!projectId) return '';
    return `${EDIT_PAGE_KEY_MAP.studio_indev_lifecycle_table}_${projectId}`;
  }, [projectId]);

  // Auto-save: get methods (check if auto-save exists)
  const autoSaveMethods: any = useAutoSaveParams({
    pageKey: lifecyclePageKey,
  });

  const {
    getSaveParamsValue,
    showRestorePrompt,
    deleteSaveParamsByPageKey,
  } = autoSaveMethods;

  // Track edit data changes for auto-save - Poll frequently to capture real-time edits
  useEffect(() => {
    if (isEditing && DCTRef.current) {
      // Poll getEditData() every 500ms to capture latest edits
      const interval = setInterval(() => {
        // Skip polling if we just did a manual update
        if (isManualUpdateRef.current) {
          isManualUpdateRef.current = false;
          return;
        }

        const currentEditData = DCTRef.current?.getEditData?.();
        if (currentEditData && Array.isArray(currentEditData)) {
          // Only update if data actually changed
          setEditData((prev: any[]) => {
            // Helper function to remove 'id' field for comparison
            // TableContent adds 'id' field internally, we need to ignore it
            const removeIdField = (data: any[]) => {
              return data.map(({ id, ...rest }) => rest);
            };

            // Deep comparison without 'id' field to avoid false positives
            const prevWithoutId = removeIdField(prev);
            const currentWithoutId = removeIdField(currentEditData);
            const prevStr = JSON.stringify(prevWithoutId);
            const currentStr = JSON.stringify(currentWithoutId);

            if (prevStr !== currentStr) {
              // Check if this is a REAL change by comparing with initial state
              const initialWithoutId = removeIdField(
                initialParamsRef.current.tableData,
              );
              const initialStr = JSON.stringify(initialWithoutId);
              const isRealChange = currentStr !== initialStr;

              // Only mark as user-edited if it's different from initial state
              if (isRealChange) {
                hasUserEditedRef.current = true;
              }

              return currentEditData;
            }
            return prev;
          });
        }
      }, 500);

      return () => clearInterval(interval);
    }
    if (!isEditing) {
      // Reset when exiting edit mode
      setEditData([]);
      hasUserEditedRef.current = false;
    }
  }, [isEditing, DCTRef]);

  // Initialize edit data when entering edit mode
  useEffect(() => {
    if (isEditing && lifecycleData && lifecycleData.length > 0) {
      // Only set editData if it's empty (first time entering edit mode)
      setEditData((prev) => {
        if (prev.length === 0) {
          return lifecycleData;
        }
        return prev;
      });
    }
  }, [isEditing]);

  // Current save params for auto-save comparison
  // Only include editData if user has actually made changes to prevent false auto-save triggers
  const currentSaveParams = useMemo(() => {
    if (!isEditing) {
      // Return empty when not editing to prevent auto-save from triggering
      return {
        tableData: [],
        projectId: '',
      };
    }

    if (!hasUserEditedRef.current) {
      // User hasn't edited yet, keep currentSaveParams same as initialParams
      // This prevents auto-save modal from showing when entering edit mode without changes
      return initialParamsRef.current;
    }

    // When editing AND user has made changes, use actual edit data
    return {
      tableData: editData.length > 0 ? editData : lifecycleData,
      projectId: projectId,
    };
  }, [isEditing, editData, lifecycleData, projectId]);

  // Update initialParams when entering edit mode (capture the original state)
  useEffect(() => {
    if (isEditing && lifecycleData.length > 0) {
      // Only update if initialParams is empty (first time entering edit mode)
      if (initialParamsRef.current.tableData.length === 0) {
        // Deep clone to avoid reference issues
        const clonedData = JSON.parse(JSON.stringify(lifecycleData));
        initialParamsRef.current = {
          tableData: clonedData,
          projectId: projectId,
        };
      }
    } else if (!isEditing) {
      // Reset initialParams when exiting edit mode
      initialParamsRef.current = {
        tableData: [],
        projectId: '',
      };
    }
  }, [isEditing, projectId]);

  useAutoSaveParams({
    pageKey: lifecyclePageKey,
    initialParams: initialParamsRef.current,
    saveParamsCur: currentSaveParams,
    isBegin: isEditing,
    isChangeValue: true,
    isEdited: hasUserEditedRef.current,
  });

  // Handler: Check for auto-save before entering edit mode
  const handleLifecycleEdit = useCallback(() => {
    if (!lifecyclePageKey) {
      DCTRef.current?.handleEdit();
      return;
    }

    // Set initialParams BEFORE entering edit mode to capture exact state
    if (
      initialParamsRef.current.tableData.length === 0 &&
      lifecycleData.length > 0
    ) {
      const clonedData = JSON.parse(JSON.stringify(lifecycleData));
      initialParamsRef.current = {
        tableData: clonedData,
        projectId: projectId,
      };
    }

    if (showRestorePrompt(lifecyclePageKey)) {
      setAutoSaveVisible(true);
    } else {
      DCTRef.current?.handleEdit();
    }
  }, [showRestorePrompt, lifecyclePageKey, DCTRef, lifecycleData, projectId]);

  // Handler: Discard auto-save and enter edit mode
  const handleAutoSaveCancel = useCallback(() => {
    setAutoSaveVisible(false);
    if (lifecyclePageKey) {
      deleteSaveParamsByPageKey(lifecyclePageKey);
    }
    DCTRef.current?.handleEdit();
  }, [deleteSaveParamsByPageKey, lifecyclePageKey, DCTRef]);

  // Handler: Restore from auto-save
  const handleAutoSaveConfirm = useCallback(async () => {
    const saveValue = getSaveParamsValue(lifecyclePageKey) || {};

    setAutoSaveVisible(false);

    // Check if projectId changed (user switched projects)
    if (saveValue.projectId && saveValue.projectId !== projectId) {
      // Clear auto-save data for the wrong project
      if (lifecyclePageKey) {
        deleteSaveParamsByPageKey(lifecyclePageKey);
      }
      DCTRef.current?.handleEdit();
      return;
    }

    // Update lifecycleData with data from auto-save
    if (
      saveValue.tableData &&
      Array.isArray(saveValue.tableData) &&
      saveValue.tableData.length > 0
    ) {
      setLifecycleData(saveValue.tableData);
    }

    // Set manual update flag to prevent polling from overwriting restored data
    isManualUpdateRef.current = true;

    // Update edit data state for auto-save tracking
    if (saveValue.tableData && Array.isArray(saveValue.tableData)) {
      setEditData(saveValue.tableData);
    }

    // Enter edit mode after state updates
    // Use setTimeout to ensure React finishes updating the component
    setTimeout(() => {
      DCTRef.current?.handleEdit();
    }, 100);
  }, [
    getSaveParamsValue,
    lifecyclePageKey,
    projectId,
    deleteSaveParamsByPageKey,
    setLifecycleData,
    DCTRef,
  ]);

  // Clear auto-save after successful save
  const clearLifecycleAutoSave = useCallback(() => {
    if (lifecyclePageKey) {
      deleteSaveParamsByPageKey(lifecyclePageKey);
    }
  }, [deleteSaveParamsByPageKey, lifecyclePageKey]);

  // Check if there are unsaved changes for RoutePrompt
  // Compare current editData with initialParamsRef
  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing || !hasUserEditedRef.current) return false;

    const initialData = initialParamsRef.current?.tableData;

    // If no initial data, no changes
    if (!initialData || initialData.length === 0) return false;

    // Get current edit data
    const currentData = editData.length > 0 ? editData : lifecycleData;

    // If both empty, no changes
    if (currentData.length === 0 && initialData.length === 0) return false;

    // Helper function to remove 'id' field for comparison
    const removeIdField = (data: any[]) => {
      return data.map(({ id, ...rest }) => rest);
    };

    // Compare data without 'id' field
    const initialWithoutId = removeIdField(initialData);
    const currentWithoutId = removeIdField(currentData);

    const hasChanges =
      JSON.stringify(initialWithoutId) !== JSON.stringify(currentWithoutId);

    return hasChanges;
  }, [isEditing, editData, lifecycleData]);

  return {
    // State
    autoSaveVisible,
    editData,
    lifecyclePageKey,
    isManualUpdateRef,
    hasUnsavedChanges,

    // State setters (for external updates)
    setEditData,

    // Handlers
    handleLifecycleEdit,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    clearLifecycleAutoSave,
  };
};
